import { useMemo } from 'react';
import type { UseQueryOptions } from '@tanstack/react-query';
import { useGenericGet } from '@hooks/use-api.hook';
import { apiClient } from '@services/api-client.service';
import { useActiveTenant } from '@states/active-tenant.atom';
import type {
  VisaApplicationReportQueryParams,
  VisaApplicationReportResponse,
} from '../types/visa-application-report.types';

const useVisaApplicationReportBasePath = () => {
  const { activeTenant } = useActiveTenant();

  return useMemo(() => {
    if (!activeTenant?.id) return '';
    return `/v1/admin/tenants/${activeTenant.id}/dashboard/reports/visa-application`;
  }, [activeTenant?.id]);
};

export const buildVisaApplicationReportUrl = (
  basePath: string,
  params: Pick<VisaApplicationReportQueryParams, 'dateRange' | 'dataSource' | 'workflowIds'>
) => {
  const query = new URLSearchParams();

  if (params.dateRange) {
    query.set('dateRange', params.dateRange);
  }

  if (params.dataSource) {
    query.set('dataSource', params.dataSource);
  }

  if (params.workflowIds && params.workflowIds.length > 0) {
    // API expects comma-separated workflow IDs in a single workflowId param
    query.set('workflowId', params.workflowIds.join(','));
  }

  const queryString = query.toString();
  return queryString ? `${basePath}?${queryString}` : basePath;
};

export const useVisaApplicationReport = (
  params: Pick<VisaApplicationReportQueryParams, 'dateRange' | 'dataSource' | 'workflowIds'>,
  queryOptions?: {
    enabled?: boolean;
    staleTime?: number;
  }
) => {
  const basePath = useVisaApplicationReportBasePath();

  const workflowKey = useMemo(
    () => (params.workflowIds && params.workflowIds.length ? params.workflowIds.join(',') : ''),
    [params.workflowIds]
  );

  const url = useMemo(() => {
    if (!basePath || !params.dateRange) return '';
    return buildVisaApplicationReportUrl(basePath, {
      ...params,
      workflowIds: workflowKey ? workflowKey.split(',') : [],
    });
  }, [basePath, params.dateRange, params.dataSource, workflowKey]);

  const queryKey: string[] = [
    'VisaApplicationReport',
    basePath,
    params.dateRange,
    params.dataSource ?? '',
    workflowKey,
  ];

  const options: UseQueryOptions<VisaApplicationReportResponse> = {
    queryKey,
    enabled: !!url && (queryOptions?.enabled ?? true),
    staleTime: queryOptions?.staleTime ?? 5 * 60 * 1000,
  };

  return useGenericGet<VisaApplicationReportResponse>(url, queryKey, apiClient, options);
};

export const useVisaApplicationReportApi = () => {
  return {
    useGet: useVisaApplicationReport,
  };
};
